import React, { useState, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { get } from "lodash";
import styled from "styled-components";

// The recipient send goes through the exit; the consumer summary copy is the same call for every exit,
// so it stays on the client here.
import client from "../../api/client/SdsWebApiClient";

import { Card, CardTitle, CardDescription, CardButton, SecondaryCardButton } from "./Cards";

import GaReporter, { GaEcommerceAction } from "../../utilities/gaReporter";
import { SET_ERROR } from "../../store/actions/errorActions";
import { getSiteUrl } from "../../utilities/navigationUtils";
import {
    getFrontendSettings,
    getSessionId,
    getShowPrice,
    getGuiTranslations,
    getAppCustomization,
    getAppConfig,
    getLanguage,
    getTouchScreen,
    getCurrentSummaries,
    getTotalPrice,
} from "../../store/sagas/selectors";
import InputFloatLabel from "../Form/InputFloatLabel";
import { InputData } from "../../models/summary/Config";
import { shouldSendSummaryEmail } from "./shouldSendSummaryEmail";
import MailIcon from "../../icons/MailIcon.svg";
import { KeyboardContext } from "../../utilities/virtual-keyboard";
import { SET_VIRTUAL_KEYBOARD } from "../../store/actions/sessionActions";
import { RetailerEmailUserInput } from "../../api/generated";
import { sanitizeHtml } from "../../utilities/htmlSanitizer";
import {
    ConsentSettings,
    appendixKey,
    buildConsentOptions,
    collectSelectedConsents,
    isAppendixEnabled,
} from "./consentLogic";
import { useConsentSelection } from "./useConsentSelection";
import { RecipientOption, SummaryRecipientExit, shopExit } from "./summaryExits";
import { RecipientCombobox } from "./RecipientCombobox";
import { effectiveRecipient as resolveEffectiveRecipient } from "./comboboxLogic";

const mailformat = /^[^@]+@[^@]+\.[^@]+$/;

interface SpanProps {
    required?: Boolean;
}

const FormContainer = styled.div`
    display: flex;
    flex-flow: column;
    align-items: flex-start;
    width: 100%;
    color: ${props => props.theme.textColorSecondary};
`;

const Checkbox = styled.input`
    display: none;
`;

const CheckBoxStyled = styled.span<CheckboxProps>`
    width: 20px;
    height: 20px;
    background: #eee;
    border-radius: 4px;
    ${props => (props.error ? "border: solid thin red;" : "")}
    :after {
        content: "";
        position: absolute;
        display: none;
        left: 6px;
        top: 2px;
        width: 5px;
        height: 10px;
        border: solid white;
        border-width: 0 3px 3px 0;
        transform: rotate(45deg);
    }
`;

const CheckboxLabel = styled.label`
    font-family: ${props => props.theme.fontFamily};
    font-size: 12px;
    align-self: flex-start;
    color: ${props => props.theme.textColorSecondary};
    margin: 2px;
    margin-top: 8px;
    text-align: left;
    display: flex;
    position: relative;
    cursor: pointer;

    &:hover input ~ ${CheckBoxStyled} {
        background: ${({ theme }) => get(theme, "summary.card.button.hoverColor", "#223861")};
    }

    input:checked + ${CheckBoxStyled} {
        background: ${({ theme }) => get(theme, "summary.card.button.backgroundColor", "#223861")};
        :after {
            display: block;
        }
    }
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const CheckSpan = styled.span<SpanProps>`
    position: relative;
    margin-left: 5px;
    max-width: calc(100% - 35px);

    ${props =>
        props.required
            ? `
        margin-left: 12px;
        ::before {
            position: absolute;
            left: -8px;
            content: '*';
        }`
            : ``}
`;

const ConsentSpan = styled.span`
    font-family: Roboto;
    font-size: 12px;
    align-self: flex-start;
    color: rgba(0, 0, 0, 0.6);
    margin: 2px;
    margin-top: 8px;
    text-align: left;
    display: flex;
    position: relative;
`;

const FormTextarea = styled.textarea<FormTextareaProps>`
    font-family: ${props => props.theme.fontFamily};
    ${({ theme }) => theme.bp.xs`
        font-size: 14px;
    `};
    ${({ theme }) => theme.bp.xl`
        font-size: 14px;
    `};
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1.2em;
                `}

    max-width: calc(100% - 20px);
    width: calc(100% - 20px);
    padding: 10px 10px;
    height: 4em;
    border-radius: 4px;
    border: 1px solid ${props => (props.error ? "red" : "#d0d0d0")};
    transition: box-shadow 0.2s ease-in-out;
    outline: none;

    @media (hover) {
        &:focus {
            box-shadow: 0px 0px 0px 1px #ccc inset;
        }
        &:hover {
            box-shadow: 0px 0px 0px 1px #ccc inset;
        }
    }

    &::placeholder {
        font-size: 14px;
    }
`;

interface FormTextareaProps {
    error?: Boolean;
}

const LabelContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const FormError = styled.span`
    font-family: ${props => props.theme.fontFamily};
    font-size: 12px;
    align-self: flex-start;
    color: red;
    margin-top: -8px;
    margin-bottom: 10px;
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const Container = styled.div<ContainerProps>`
    visibility: ${props => (props.visible ? "visible" : "hidden")};
`;

const FeedbackContainer = styled.div<ContainerProps>`
    visibility: ${props => (props.visible ? "visible" : "hidden")};
    ${({ theme }) => theme.bp.xs`
        width: calc(100% - 44px);
        padding: 20px;
        height: calc(100% - 40px);
    `};
    ${({ theme }) => theme.bp.sm`
        width: calc(100% - 24px);
        padding: 10px;
        height: calc(100% - 20px);
    `};
    ${({ theme }) => theme.bp.xxl`
        width: calc(100% - 64px);
    `};
    position: absolute;
    bottom: 0;
    padding: 10px;
    width: calc(100% - 64px);
    height: calc(100% - 20px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
`;

const FeedbackContent = styled.div``;

const SentIcon = styled.div`
    margin-top: 15px;
    svg {
        fill: currentColor;
        color: var(--primary-color);
`;

interface ContainerProps {
    visible: Boolean;
}

interface CheckboxProps {
    error: Boolean;
}

const SendCustomSummaryCard = ({ config, exit = shopExit }: { config: any; exit?: SummaryRecipientExit }) => {
    const dispatch = useDispatch();
    const sessionId = useSelector(getSessionId);
    const frontendSettings = useSelector(getFrontendSettings);
    const showPrice = useSelector(getShowPrice);
    const appConfig = useSelector(getAppConfig);
    const language = useSelector(getLanguage);
    const guiTranslations = useSelector(getGuiTranslations);
    const appCustomization = useSelector(getAppCustomization);
    const summaryGroups = useSelector(getCurrentSummaries);
    const totalPrice = useSelector(getTotalPrice);
    const touchScreen = useSelector(getTouchScreen);
    const VirtualKeyboard = useContext(KeyboardContext);
    const allItems = summaryGroups.flatMap(g => g.items);

    const recipients = exit.selectRecipients(appCustomization);
    const initialRecipient = recipients.length > 0 ? recipients[0] : undefined;
    // appCustomization is null for sessions without an account (no customization.json) — guard so the card degrades to no consent boxes instead of crashing.
    const consentSettings = (appCustomization?.FrontendSettings || {}) as ConsentSettings;
    const consentOptions = buildConsentOptions(consentSettings, exit.consentPolicy.section);
    const appendixEnabled = isAppendixEnabled(consentSettings, exit.consentPolicy.section);

    const [activeRecipient, setActiveRecipient] = useState<RecipientOption | undefined>(initialRecipient);

    // The recipient that will actually be mailed. The combobox filters its own list internally; a typed
    // query never changes this — only an explicit selection does. "What's shown is what's sent."
    const effectiveRecipient = resolveEffectiveRecipient(activeRecipient, recipients);
    const [userInputs, setUserInputs] = useState<any>({});
    const [errors, setErrors] = useState<any>({});
    const [isDisabled, setIsDisabled] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const { checkedConsents, consentErrors, toggleConsent, validateRequiredConsents } =
        useConsentSelection(consentOptions);

    const searchParams = new URLSearchParams(window.location.search);
    const shopEmail = searchParams.get("shop_email");

    const inputFields: InputData[] = config.inputs?.filter((input: InputData) => input.enabled !== false) || [];

    const checkIfPossibleToSendEmail = () => {
        let shouldSendEmail = true;
        const inputErrors: { [key: string]: string } = {};

        inputFields.forEach(field => {
            if (field.id === "email" && userInputs.email && !userInputs.email.match(mailformat)) {
                shouldSendEmail = false;
                inputErrors[field.id] = get(appConfig, `translations.${language}.sendToShopInvalidEmail`);
            } else if (field.required && !userInputs[field.id]) {
                shouldSendEmail = false;
                inputErrors[field.id] = get(appConfig, `translations.${language}.sendToShopRequiredField`, "Required");
            }
        });

        // Required consents left unticked get a red outline and block the send.
        if (!validateRequiredConsents()) {
            shouldSendEmail = false;
        }

        setErrors(inputErrors);
        return shouldSendEmail;
    };

    const sendRecipientEmail = async () => {
        try {
            const shouldSendEmail = checkIfPossibleToSendEmail();
            if (!sessionId || !frontendSettings || !shouldSendEmail || !effectiveRecipient) return;

            setIsDisabled(true);

            const customerInfo: RetailerEmailUserInput[] = inputFields.map(field => ({
                id: field.id,
                title: field.title,
                value: userInputs[field.id] || "",
            }));

            const consents = collectSelectedConsents(
                consentOptions,
                checkedConsents,
                appendixEnabled,
                exit.consentPolicy
            );

            const siteUrl = getSiteUrl();
            await exit.send({
                sessionId,
                recipient: effectiveRecipient,
                userInputs: customerInfo,
                consents,
                hidePdfPrice: !showPrice,
                siteUrl,
            });

            setEmailSent(true);

            // NGOP-1491: best-effort — also email the PDF summary to the consumer. Runs right after the
            // success UI and BEFORE analytics, so a GaReporter throw (GA does real work in prod) can't
            // skip it; failure is swallowed so it never disturbs the already-successful send-to-shop.
            const emailField = inputFields.find(field => field.id === "email");
            const summaryEmail = emailField && userInputs[emailField.id];
            if (
                summaryEmail &&
                shouldSendSummaryEmail(emailField, summaryEmail, appCustomization?.SendSummaryCopyToUserEnabled)
            ) {
                try {
                    // Omit consents entirely (don't pass []): an empty array is truthy in the generated
                    // client, so it serializes to `?consents=`, which the backend binds to [""] and then
                    // 500s trying to build a consent mail for an empty id. No consents = no param.
                    await client.sendCustomerMailPost(sessionId, summaryEmail, undefined, !showPrice, siteUrl);
                } catch (e) {
                    // Best-effort: don't disturb the successful send-to-shop, but surface for diagnosis.
                    // eslint-disable-next-line no-console
                    console.warn("NGOP-1491: customer summary send failed for", summaryEmail, e);
                }
            }

            GaReporter.event(exit.gaEvent, "CTA", undefined, exit.gaPayload(effectiveRecipient));
            GaReporter.trackProductCheckout(
                GaEcommerceAction.Purchase,
                exit.gaCheckoutAction,
                allItems,
                showPrice ? totalPrice : null
            );
        } catch (e) {
            setIsDisabled(false);
            dispatch({
                type: SET_ERROR,
                error: {
                    message: get(appConfig, `translations.${language}.sendToShopFailure`),
                },
            });
        }
    };

    const renderInputField = (input: InputData) => {
        const setUserInput = (value: string) => {
            setUserInputs({ ...userInputs, [input.id]: value });
        };

        if (input.id === "message") {
            const onFormFocus = (value: boolean, currentValue: string) => {
                if (!touchScreen) return;

                if (value) {
                    VirtualKeyboard.show(inputs => setUserInput(inputs), currentValue);
                } else {
                    VirtualKeyboard.hide();
                }

                dispatch({ type: SET_VIRTUAL_KEYBOARD, showVirtualKeyboard: value });
            };

            // Wrapped like the labelled inputs below so the message carries its error text and
            // not only the red outline. The textarea names itself through a placeholder, which
            // disappears as soon as anything is typed, so the outline alone leaves a
            // filled-but-rejected message with nothing saying what is wrong.
            return (
                <LabelContainer key={input.id}>
                    <FormTextarea
                        value={userInputs[input.id]}
                        error={errors[input.id]}
                        onChange={event => setUserInput(event.target.value)}
                        placeholder={input.required ? `*${input.title}` : input.title}
                        onFocus={event => onFormFocus(true, event.target.value)}
                        onBlur={event => onFormFocus(false, event.target.value)}
                    />
                    {errors[input.id] ? <FormError>{errors[input.id]}</FormError> : null}
                </LabelContainer>
            );
        }

        return (
            <LabelContainer key={input.id}>
                <InputFloatLabel
                    name={input.title}
                    error={errors[input.id]}
                    setEvent={setUserInput}
                    required={input.required}
                />
                {errors[input.id] ? <FormError>{errors[input.id]}</FormError> : null}
            </LabelContainer>
        );
    };

    return (
        <Card>
            <Container visible={!emailSent}>
                <CardTitle>{config.title}</CardTitle>
                {config.description ? (
                    <CardDescription dangerouslySetInnerHTML={{ __html: sanitizeHtml(config.description) }} />
                ) : (
                    <></>
                )}

                <FormContainer>
                    {shopEmail || recipients.length < 2 ? (
                        <></>
                    ) : (
                        <RecipientCombobox
                            label={get(
                                appConfig,
                                `translations.${language}.${exit.dropdownLabelKey}`,
                                exit.dropdownLabelFallback
                            )}
                            searchPlaceholder={get(
                                appConfig,
                                `translations.${language}.sendInShopSalespersonSearch`,
                                "Search"
                            )}
                            noResultsText={get(
                                appConfig,
                                `translations.${language}.sendInShopSalespersonNoResults`,
                                "No matches"
                            )}
                            options={recipients}
                            value={effectiveRecipient}
                            onChange={setActiveRecipient}
                        />
                    )}

                    {inputFields.map(inputData => renderInputField(inputData))}

                    {consentOptions.map(option => (
                        <CheckboxLabel key={option.key}>
                            <Checkbox
                                type="checkbox"
                                checked={!!checkedConsents[option.key]}
                                onChange={toggleConsent(option.key)}
                                aria-invalid={consentErrors.includes(option.key)}
                            />
                            <CheckBoxStyled error={consentErrors.includes(option.key)} />
                            <CheckSpan required={option.required}>{guiTranslations.get(option.key)}</CheckSpan>
                        </CheckboxLabel>
                    ))}

                    {/* The appendix is informational text (no checkbox); recorded whenever enabled. */}
                    {appendixEnabled ? (
                        <ConsentSpan>{guiTranslations.get(appendixKey(exit.consentPolicy.section))}</ConsentSpan>
                    ) : null}
                </FormContainer>
                <CardButton
                    isDisabled={isDisabled}
                    onClick={() => {
                        sendRecipientEmail();
                    }}
                >
                    <p>{config.buttonText}</p>
                </CardButton>
            </Container>

            <FeedbackContainer visible={emailSent}>
                <CardTitle>{config.title}</CardTitle>
                <FeedbackContent>
                    <SentIcon>
                        <MailIcon width={40} />
                    </SentIcon>
                    <CardDescription>{config.emailSentDescription}</CardDescription>
                </FeedbackContent>
                <SecondaryCardButton
                    onClick={() => {
                        setEmailSent(false);
                        setIsDisabled(false);
                    }}
                >
                    <p>{config.feedbackButtonText}</p>
                </SecondaryCardButton>
            </FeedbackContainer>
        </Card>
    );
};

export default SendCustomSummaryCard;
